/* eslint-disable */
// CLB — Carolina Lemke Berlin Operations Hub v3
if ('serviceWorker' in navigator) navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SpeedInsights } from "@vercel/speed-insights/react";
import * as Sentry from "@sentry/react";
import { supabase } from "./supabase";
import {
  STAGES, DEV_STAGE_STATUS, STAGE_COLORS, devStageColor,
  TYPE_COLORS, STATUS_COLORS, PRIORITY_COLORS, DEV_REQ_STAGES,
  flex, flexBetween, pill,
  ThemeCtx, useT,
  DEV_FULL_STATUS, STOPPED_STATUSES,
  getNextStatus, isDevStopped, isDevCompleted, isTransitionStatus,
  STATUS_NORMALIZE_MAP, normalizeStatus, STAGE_NORMALIZE_MAP, normalizeStageStatus,
  isOverdue, DEFAULT_OVERDUE_RULES,
  DEV_TYPES_LIST, DEV_SEASONS, DEV_MATERIALS, DEV_FACTORIES, DEV_BASED_ON_LIST,
  DEFAULT_DEV_FIELDS, generateDevNumber,
  SHAPE_OPTIONS, DEFAULT_FIELDS,
  THEME_PRESETS, FONT_PRESETS, DEFAULT_THEME, STATUS_CONF, ROLES
} from "./shared";
import { useIsMobile } from "./utils";
import { useIsAdmin } from "./components/developments/settings/hooks/useIsAdmin";
import { useThemeStyles } from "./hooks/useThemeStyles";
import "./styles/app-keyframes.css";
import "./styles/qc-responsive.css";
import { SetPasswordScreen } from "./LoginScreen";
import { EntryLoginScreen } from "./components/EntryLoginScreen";
import { PortalSelection } from "./components/PortalSelection";
import { SplashScreen } from "./components/SplashScreen";
import { PORTAL_TAB_MAP } from "./portalConfig";
import { DevRequestModal } from "./DevRequests";
import { DevRequestNotification } from "./portals/devRequests/DevRequestNotification";
import { ExportModal } from "./ExportModal";
import { Lightbox } from "./Lightbox";
import { AppOverlays } from "./components/system/AppOverlays";
import { FullScreenGallery, ImportModal, HoverPreview } from "./components/CatalogModals";
import { FrameDetailModal } from "./components/originals/FrameDetailModal";
import shellStyles from './components/AppShell.module.css';
import catalogChromeStyles from "./components/catalog/CatalogChrome.module.css";
import devsChromeStyles from "./components/developments/DevsChrome.module.css";
import { EditFrameModal, QuickAddModal } from "./components/FrameModals";
import { NewItemModal } from "./components/originals/NewItemModal";
import { TabRouter } from "./components/TabRouter";
import { useToast, ToastContainer } from "./Toast";
import { useConfirmDialog } from "./ConfirmDialog";
import { useCatalogFilters, applyFilters, applySortOrder, hasActiveFilters, DEV_NUMBER_RE } from "./hooks/useCatalogFilters";
import { useAppData } from "./hooks/useAppData";
import { useLinkedDevsMap } from "./hooks/useLinkedDevsMap"; import { useDeadlineDevs } from "./hooks/useDeadlineDevs";
import { useItemHandlers } from "./hooks/useItemHandlers";
import { useAuthSetup } from "./hooks/useAuthSetup";
import { useSessionTracking } from "./hooks/useSessionTracking";
import { useDerivedData } from "./hooks/useDerivedData";
import { useFavorites } from "./hooks/useFavorites";
import { LoadingSplash } from "./components/LoadingSplash";
import { usePortalPermissions } from "./hooks/usePortalPermissions";
import { isSuperAdmin, clearSuperAdminCache } from "./utils/superAdmin";
import { useUiStore, DEFAULT_DEV_VIEW } from "./stores/uiStore";
import { ShortcutsModal } from "./components/ShortcutsModal";
import NetworkErrorToast from "./components/NetworkErrorToast";
import { RealtimeTestHarness } from "./components/RealtimeTestHarness";
import { useDevelopmentsRealtime } from "./hooks/useDevelopmentsRealtime";
import { useQcRealtime } from "./hooks/useQcRealtime";
import { useCatalogRealtime } from "./hooks/useCatalogRealtime";
import { useActivityLogRealtime } from "./hooks/useActivityLogRealtime";
import { useUserPermissionsRealtime } from "./hooks/useUserPermissionsRealtime";
import { ResetLanding } from "./components/auth/ResetLanding";
import ShellRoot from "./components/shell/ShellRoot"; import { useShellStore } from "./components/shell/shellStore"; // Shell v3 (CLB-012) — the only shell since David's sign-off 12.10; the legacy one is in src/_archive/shell-v2/

// ── Root — auth wrapper ──────────────────────────────────────────────────────
export default function Root() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authEvent, setAuthEvent] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  // Phase C — scanner-proof recovery: the branded email links to
  // ?reset_token_hash= on OUR site; only the Continue click verifies the OTP.
  const [resetTokenHash, setResetTokenHash] = useState(() => new URLSearchParams(window.location.search).get("reset_token_hash"));
  const [recoveryVerified, setRecoveryVerified] = useState(false);
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      // T-026-B: sync realtime client with user JWT BEFORE App mounts so the
      // first phx_join carries the correct token (closes anon-JOIN race).
      try {
        await supabase.realtime.setAuth(data.session?.access_token ?? null);
      } catch (err) {
        Sentry.captureMessage("realtime.setAuth.initial_failed", {
          level: "error",
          extra: { error: err?.message },
        });
      }
      setUser(data.session?.user || null);
      setAuthChecked(true);
    });
    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      // T-026-B: keep realtime client's token in sync on every auth lifecycle event
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") {
        try {
          await supabase.realtime.setAuth(session?.access_token ?? null);
        } catch (err) {
          Sentry.captureMessage(`realtime.setAuth.${event.toLowerCase()}_failed`, {
            level: "error",
            extra: { error: err?.message },
          });
        }
      } else if (event === "SIGNED_OUT") {
        try {
          await supabase.realtime.setAuth(null);
        } catch (_) {
          /* benign on signout */
        }
      }

      if (event === "TOKEN_REFRESHED" && !session) {
        // T-1025 — wrap fire-and-forget signOut: Supabase v2 auth uses navigator.locks
        // and can reject with AbortError when lock is stolen by a concurrent operation.
        // Without .catch(), the rejection becomes an unhandledrejection (Sentry showed
        // 21 such events over 45 days). This branch fires on stale-session token refresh
        // and can race the user's own signOut click — same root-cause class as line 136.
        supabase.auth.signOut().catch((e) => {
          Sentry.captureException(e, {
            tags: { feature: "auth", op: "signOut", reason: "token-refresh-stale-session" },
          });
        });
        setUser(null);
        return;
      }
      if (event === "SIGNED_OUT") { clearSuperAdminCache(); window.location.reload(); return; }
      setUser(session?.user || null);
      setAuthEvent(event);
      if (event === "SIGNED_IN" && session?.user) {
        supabase.from("user_profiles").select("must_change_password").eq("id", session.user.id).single()
          .then(({ data }) => { if (data?.must_change_password) setMustChangePassword(true); });
      }
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  const handlePasswordChanged = async () => {
    if (user) await supabase.from("user_profiles").update({ must_change_password: false }).eq("id", user.id);
    setMustChangePassword(false); setAuthEvent(null);
  };

  if (!authChecked) {
    return <LoadingSplash context="Operations Hub" label="Loading workspace" />;
  }

  // Phase C — interstitial first (additive; legacy PASSWORD_RECOVERY path below stays as fallback)
  if (resetTokenHash) return <ResetLanding tokenHash={resetTokenHash}
    onVerified={() => { setResetTokenHash(null); setRecoveryVerified(true); }}
    onCancel={() => setResetTokenHash(null)} />;
  if (recoveryVerified) return user
    ? <SetPasswordScreen title="RESET PASSWORD" subtitle="Enter your new password below" onDone={() => { setRecoveryVerified(false); handlePasswordChanged(); }} />
    : <LoadingSplash context="Operations Hub" label="Verifying reset link" />;
  if (authEvent === "PASSWORD_RECOVERY" || authEvent === "password_recovery") return <SetPasswordScreen title="RESET PASSWORD" subtitle="Enter your new password below" onDone={handlePasswordChanged} />;
  if (user && mustChangePassword) return <SetPasswordScreen title="SET YOUR PASSWORD" subtitle="Please set a personal password to continue" onDone={handlePasswordChanged} />;
  if (!user) return <EntryLoginScreen onLogin={setUser} />;
  return <App user={user} onLogout={() => {
    // T-1025 Stages 2-5 — robust signout, evolved across 4 commits as each
    // empirical signal unmasked the next layer:
    //   Stage 2 (fb30cb4): try/catch the AbortError from navigator.locks
    //     contention (Sentry JAVASCRIPT-REACT-5, 21 events / 45 days, handled:no)
    //   Stage 3 (1d6a9a3): full storage clear instead of sb-* filter (a non-
    //     sb-* key was restoring the session)
    //   Stage 4 (8e0e52c): reload() not href='/' (no-op when already at root)
    //   Stage 5 (this): NO await on signOut — fire-and-forget. Awaiting
    //     allowed Supabase to fire SIGNED_OUT synchronously, racing the
    //     listener at line 107 (which also did href='/' = no-op) against
    //     this finally block. Removing await guarantees synchronous progression:
    //     fire signOut → clear storage → reload, no race possible.
    // The reload re-inits the app; the auth check sees empty storage = logged out.
    supabase.auth.signOut().catch((e) => {
      Sentry.captureException(e, {
        tags: { feature: "auth", op: "signOut", reason: e?.name || "unknown" },
      });
    });
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (_cleanupErr) {
      // Storage may be locked / quota-exceeded — ignore; reload will reset.
    }
    window.location.reload();
  }} />;
}

// ── App ──────────────────────────────────────────────────────────────────────
function App({ user, onLogout }) {
  // ── Zustand UI store (replaces 25+ local useState) ──
  const ui = useUiStore();
  const { tab, entryPhase, transitionPortal, devView, devSearch, devFilters, devSort, showDevForm, devFormPrefill, devFormRequestLink, filteredDevsList, filteredIgnoringStatusList, viewMode, selectedItems, showExport, showBrandManager, showActionsMenu, filterPanelOpen, devRequestItem, devReqNotification, fullScreen, detailImgIdx, fsImgIdx, fsZoomed, lightboxSrc, hoverPreview, toast, showShortcuts } = ui;
  const shellChromeH = useShellStore(s => s.appBarH + s.filterBarH + s.railH); // Shell v3: the chrome height becomes --app-header-height (S0 §0.2)

  // ── Catalog Filters (extracted hook) ──
  const {
    search, setSearch, debouncedSearch,
    fBrand, setFBrand, fGender, setFGender, fType, setFType, fMat, setFMat,
    fStatus, setFStatus, fShape, setFShape, fAssigned, setFAssigned, fCreatedBy, setFCreatedBy,
    sortBy, setSortBy,
    filtersRef, clearAll: clearAllFilters, activeFilterCount, isCurrentlyFiltered,
  } = useCatalogFilters();

  const [favorites, setFavorites] = useState(() => { try { return JSON.parse(localStorage.getItem("clb_favorites")||"{}"); } catch(e) { return {}; } });

  // ── Data layer ──
  const {
    items, setItems, totalCount, setTotalCount, allItemsStats, refreshAllItemsStats, linksSlim,
    pageLoading, loaded, devs, setDevs, devSettings, setDevSettings,
    fields, setFields, theme, setTheme, catalogViewSize, setCatalogViewSize,
    devRequests, setDevRequests, devRequestsLoaded,
    fetchPagedItems, fetchAllItemsRaw, loadMoreItems, loadDevsIfNeeded, reloadDevs,
  } = useAppData({ user, filtersRef, onShowToast: ui.showToast });

  // T-1015 — bidirectional BASED ON. Build Map<item_id, dev[]> from devs and
  // inject into filtersRef.current so applyFilters' reverse-lookup branch can
  // see it. useCatalogFilters' own effect uses spread-merge to preserve this
  // injected key across filter-state-change rebuilds.
  // app-shell 2026-09-14: devs = the full list where a screen loaded it (Developments), else the slim
  // link facts (useDevLinksSlim, Catalog / Overview); items = the slim stats read, else the page.
  const linkedDevsMap = useLinkedDevsMap(devs.length ? devs : linksSlim, allItemsStats.length > 0 ? allItemsStats : items);
  useEffect(() => {
    filtersRef.current = { ...filtersRef.current, linkedDevsMap };
  }, [linkedDevsMap, filtersRef]);

  // T-1031 — 3-state for the Catalog reverse-search banner:
  //   'matched'      — DA/DO/DS pattern AND linkedDevsMap has >=1 hit
  //   'fallthrough'  — DA/DO/DS pattern BUT linkedDevsMap empty (sf branch runs)
  //   'none'         — input is not a dev-number pattern (no banner)
  // Mirrors the iteration in applyFilters (useCatalogFilters.js) so the banner
  // text stays in sync with the underlying dispatch outcome. Computed from raw
  // `search` (not debouncedSearch) so the banner reflects what the user typed
  // immediately.
  const linkedDevQuery = useMemo(() => {
    const s = (search || "").trim();
    return DEV_NUMBER_RE.test(s) ? s.toUpperCase() : null;
  }, [search]);
  const linkedDevMode = useMemo(() => {
    if (!linkedDevQuery) return 'none';
    if (!linkedDevsMap) return 'fallthrough';
    const sUpper = linkedDevQuery.replace(/\s+/g, "");
    for (const devs of linkedDevsMap.values()) {
      for (const d of devs) {
        const devNum = (d.dev_number || d.data?.dev_number || "").toUpperCase();
        if (devNum.includes(sUpper)) return 'matched';
      }
    }
    return 'fallthrough';
  }, [linkedDevQuery, linkedDevsMap]);

  // ── Prefetch Sales data ──
  const qcPrefetch = useQueryClient();
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => { qcPrefetch.prefetchQuery({ queryKey: ['sales-data-range'], queryFn: async () => { const { data } = await supabase.rpc('get_data_range'); return data; }, staleTime: 3 * 60 * 1000 }); }, 3000);
    return () => clearTimeout(t);
  }, [loaded]);

  // ── Item handlers ──
  const {
    saving, form, setForm, editing, setEditing,
    showAdd, setShowAdd, showQuickAdd, setShowQuickAdd, quickForm, setQuickForm,
    showImport, setShowImport, importPreview, setImportPreview,
    imageUploading, setImageUploading, selected, setSelected,
    recentlyUploaded, comments, commentText, setCommentText, commentsLoading,
    addComment, handleAdd, handleSaveEdit, handleDelete, handleImportFile, handleImportConfirm,
  } = useItemHandlers({ user, myProfile: null, items, setItems, setTotalCount, refreshAllItemsStats, fields, showToast: ui.showToast });

  // ── Auth / profile ──
  const { role, myProfile, userNameMap, allUsers, myDesignerNames, allDesignerNames, myDesignerIdentity } = useAuthSetup({ user, setSortBy, setFavorites });

  // ── Permissions ──
  const { data: permsData, isLoading: permsLoading } = usePortalPermissions();
  const portalPerms = permsData?.permissions || [];
  const isDavid = isSuperAdmin(user);
  const canAccessPortal = (portalId) => {
    if (isDavid) return true;
    if (permsLoading) return false;
    const p = portalPerms.find(x => x.portal_id === portalId);
    return p ? p.role !== 'none' : false;
  };

  // Home remains the landing surface for every account, including one-workspace
  // users: their libraries, personal arrangement and updates still belong here.

  const activePortal = tab === "developments" ? "developments" : "catalog";

  // ── Derived ──
  const [devOverdueCount, setDevOverdueCount] = useState(0);
  const activeDevs = useMemo(() => devs.filter(d=>!(d.archived||d.data?.archived)), [devs]); const bellDevs = useDeadlineDevs(entryPhase === 'app'); const headerDevs = devs.length ? activeDevs : bellDevs;   // app-shell 2026-09-14: the bell reads its own slim list (never on the Hub, which has no header) until a screen loads the full one
  useEffect(() => { setDevOverdueCount(headerDevs.filter(d=>isOverdue(d,DEFAULT_OVERDUE_RULES)).length); }, [devs, bellDevs]);

  // Stage 2 deadline notifications — designer-scoped dev list for the bell
  // panel. useIsAdmin returns true for super_admin OR developments-portal
  // admin (mirror of _settings_is_admin() SQL). Non-admins see only devs whose
  // data.designer matches one of their user_profiles.designer_names (case-
  // insensitive — same predicate as useDevFilters.js:76).
  const isDeadlineAdmin = useIsAdmin();
  const scopedDevsForDeadline = useMemo(() => {
    if (isDeadlineAdmin) return headerDevs;
    if (!myDesignerNames || myDesignerNames.length === 0) return [];
    return headerDevs.filter(d => {
      const designer = (d.designer || d.data?.designer || '').toLowerCase();
      return myDesignerNames.includes(designer);
    });
  }, [headerDevs, isDeadlineAdmin, myDesignerNames]);
  const isMobile = useIsMobile();
  const perm = ROLES[isDavid ? 'admin' : role];
  const T = theme;
  const drNotifCheckedRef = useRef(false);
  const { confirm: confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const searchInputRef = useRef(null);

  // ── Session tracking ──
  useSessionTracking({ user, onLogout });

  // ── Realtime: developments table → tiered React Query invalidation ──
  // (Brief 03 Phase 2. Coexists with the legacy subscription in
  //  DevelopmentsTab.js:179, which keeps App-level devs useState in sync.
  //  Phase 7 will remove the legacy one.)
  useDevelopmentsRealtime();

  // ── Realtime: QC tables (qc_reports, qc_items, qc_issues) → tiered
  //    React Query invalidation. T-025 Phase 3. Same pattern as Phase 2.
  useQcRealtime();

  // ── Realtime: items table → React Query invalidation only
  //    (['items'] + ['portal-counts']). T-026 Phase 4. Sub-second grid
  //    sync is handled by the legacy channel bridge in useAppData.js
  //    ('items-changes') because the Catalog grid is plain useState, not
  //    React Query (Phase 7 will migrate). See useCatalogRealtime.js
  //    header comment for the Option B layered architecture rationale.
  useCatalogRealtime();

  // ── Realtime: activity_log table (INSERT-only — append-only by
  //    Convention #56) → tiered React Query invalidation. T-027 Phase 5.
  //    Tier A broad: Activity Feed, Hub ledger, Hub stats.
  //    Tier B (10s debounce): get_dev_handling, get_team_productivity,
  //    get_activity_stats, analytics_activity_feed,
  //    analytics_designer_pace_monthly. See useActivityLogRealtime.js
  //    header for the Phase 2 / Phase 5 analytics overlap note.
  useActivityLogRealtime();

  // Access-engine Step 1 — reactive permission sync. Grants/revokes reflect in the
  // current user's nav/hub without a re-login (RLS-scoped realtime; own rows only).
  useUserPermissionsRealtime();

  // Font loader
  useEffect(() => {
    const fp = FONT_PRESETS.find(f => f.name === T.fontPreset);
    if (fp && fp.gf) { const id = "clb-font-" + fp.name; if (!document.getElementById(id)) { const link = document.createElement("link"); link.id=id; link.rel="stylesheet"; link.href=`https://fonts.googleapis.com/css2?family=${fp.gf}&display=swap`; document.head.appendChild(link); } }
  }, [T.fontPreset]);

  // Cross-component events
  useEffect(() => {
    const handler = async (e) => {
      const itemId = e.detail?.id; if (!itemId) return;
      let item = items.find(i => i.id === itemId);
      if (!item) { const { data, error } = await supabase.from("items").select("*").eq("id", String(itemId)).single(); if (!error && data) item = { ...data.data, id: data.id }; }
      if (item) { ui.setTab("catalog"); setTimeout(() => setSelected(item), 100); }
    };
    window.addEventListener("open_catalog_item", handler);
    return () => window.removeEventListener("open_catalog_item", handler);
  }, [items]);

  useEffect(() => {
    const handler = (e) => { if (e.detail?.id) ui.setTab("developments"); };
    window.addEventListener("open_development", handler);
    return () => window.removeEventListener("open_development", handler);
  }, []);

  // Dev request notifications
  useEffect(() => {
    if (!devRequestsLoaded || drNotifCheckedRef.current || !user || role === 'admin' || !devRequests?.length || !myProfile) return;
    const myName = myProfile.my_designer_name || ''; if (!myName) return;
    drNotifCheckedRef.current = true;
    const unseen = devRequests.find(r => r.status === 'pending' && !(r.seen_by || []).includes(user.email) && r.created_by !== user.email && (r.assigned_to || []).some(name => name.toLowerCase() === myName.toLowerCase()));
    if (unseen) ui.setDevReqNotification(unseen);
  }, [devRequestsLoaded, devRequests, user?.email, role, myProfile]);

  useEffect(() => {
    if (!user || role === 'admin' || !myProfile?.my_designer_name) return;
    const myName = myProfile.my_designer_name;
    const channel = supabase.channel('app-dr-notif').on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'dev_requests' }, (payload) => {
      const req = payload.new; if (req.created_by === user.email) return;
      if ((req.assigned_to || []).some(name => name.toLowerCase() === myName.toLowerCase())) ui.setDevReqNotification(req);
    }).subscribe();
    return () => supabase.removeChannel(channel);
  }, [user?.email, role, myProfile?.my_designer_name]);

  useEffect(() => {
    const handler = (e) => {
      const { prefill, requestId, requestedModel } = e.detail || {}; if (!prefill) return;
      ui.setDevFormPrefill(prefill);
      ui.setDevFormRequestLink(requestId ? { requestId, model: requestedModel || '' } : null);
      ui.setTab("developments"); ui.setShowDevForm(true);
    };
    window.addEventListener("create_dev_from_request", handler);
    return () => window.removeEventListener("create_dev_from_request", handler);
  }, []);

  // Filter/search re-fetch
  const filterFetchRef = useRef(false);
  useEffect(() => {
    if (!filterFetchRef.current) { filterFetchRef.current = true; return; }
    ui.setSelectedItems(new Set()); fetchPagedItems(0, false);
  }, [debouncedSearch, fBrand, fGender, fType, fMat, fStatus, fShape, fAssigned, fCreatedBy, sortBy]);

  useEffect(() => { loadDevsIfNeeded(ui.showGlobalSearch ? "search" : entryPhase === "app" ? tab : ""); }, [tab, entryPhase, ui.showGlobalSearch, loadDevsIfNeeded]);   // never on the Hub; search reads the dev rows — its own slim read is Stage 1b

  // ── Derived data ──
  const { allBrands, genderOpts, typeOpts, matOpts, statusOpts, shapeOpts, assignedOpts, createdByOpts, pagedItems, catalogCols, hasMore, stats } =
    useDerivedData({ items, allItemsStats, totalCount, fields, catalogViewSize, isCurrentlyFiltered, role });
  const { toggleFavorite, isFav } = useFavorites({ favorites, setFavorites, userId: user.id });
  const { br, br75, inputSt, cardSt, lblSt, btnP, btnS } = useThemeStyles(T, isMobile);
  const _myIdentity = myProfile?.my_designer_name || '';
  const devRequestBadge = role === 'admin' ? (devRequests || []).filter(r => r.status === 'pending').length
    : !_myIdentity ? 0 : (devRequests || []).filter(r => r.status === 'pending' && r.created_by !== user?.email && (r.assigned_to || []).some(name => name.toLowerCase() === _myIdentity.toLowerCase())).length;

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      // Cmd+K / Ctrl+K — OPENS (never toggles), fires even from inputs. Escape closes.
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); ui.setShowGlobalSearch(true); return; }
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (e.key === "?") { ui.setShowShortcuts(true); return; }
      if (e.key === "/") { e.preventDefault(); if(searchInputRef.current) searchInputRef.current.focus(); else ui.setShowGlobalSearch(true); }
      if (e.key === "Escape") { ui.setShowShortcuts(false); ui.setShowGlobalSearch(false); ui.setShowActionsMenu(false); ui.setFilterPanelOpen(false); setSelected && typeof setSelected === "function" && setSelected(null); }
      if (e.key === "n" && !e.metaKey && !e.ctrlKey) { if (tab === "catalog" && perm?.canAdd) setShowAdd(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [tab, perm, role]);

  // ONE overlay element, built above every early return so the same mount serves
  // the Hub, the splash and the portal chrome — its position decides only WHETHER
  // it renders, never how it looks (it portals to body, Conv #126/#142). The four
  // returns below are mutually exclusive, so this is never two live instances.
  const appOverlays = <AppOverlays fields={fields} role={role} setSelected={setSelected} setFBrand={setFBrand} />;

  if (!loaded) return <>{appOverlays}<LoadingSplash context="Operations Hub" label="Loading workspace" /></>;
  // Home owns its permission loading/error state. Keep it mounted through a retry
  // so its query observer cannot repeatedly restart a failed read on mount.
  if (permsLoading && entryPhase !== 'selection') return <>{appOverlays}<LoadingSplash context="Operations Hub" label="Loading permissions" /></>;

  const displayName = myProfile?.display_name || user?.email?.split('@')[0] || 'User';
  // T-1001 Strategy α: reset Developments sub-state on cold entry so first
  // paint after login/splash lands on DEFAULT_DEV_VIEW, not last session's view.
  const _resetDevSubState = () => {
    ui.setDevView(DEFAULT_DEV_VIEW);
    ui.setAnalyticsView(null);
    ui.setSettingsView(null);
    ui.setSettingsPanel(null);
  };
  if (entryPhase === 'splash') return <>{appOverlays}<SplashScreen portalId={transitionPortal} userName={displayName} onComplete={(id) => { _resetDevSubState(); ui.setTab(PORTAL_TAB_MAP[id] || 'catalog'); ui.setEntryPhase('app'); }} /></>;
  // Brief 3 — DANA-era portal-entry interstitial removed (~4.8s cosmetic
  // theater, zero real work). Portal click now enters the app instantly via
  // the same setTab + setEntryPhase('app') logic that the removed
  // interstitial's onComplete used to fire, hoisted into onSelectPortal
  // below. The 'transition' entryPhase value is no longer reachable from
  // anywhere in src/ (verified by grep) but the setter + transitionPortal
  // slice stay for compatibility with the retained splash fallback above.
  if (entryPhase === 'selection') return <>{appOverlays}<PortalSelection user={user} role={role} myProfile={myProfile} onSelectPortal={(id) => { _resetDevSubState(); ui.setTab(PORTAL_TAB_MAP[id] || 'catalog'); ui.setEntryPhase('app'); }} onOpenSearch={() => ui.setShowGlobalSearch(true)} onSignOut={onLogout} /></>;

  const catalogTabs = ["catalog","dashboard","analytics","activity","favorites","my_profile","users","settings","sales"];
  if (catalogTabs.includes(tab) && !canAccessPortal('catalog')) { ui.setEntryPhase('selection'); return null; }
  if (tab === 'developments' && !canAccessPortal('developments')) { ui.setEntryPhase('selection'); return null; }
  if (tab === 'qc' && !canAccessPortal('qc')) { ui.setEntryPhase('selection'); return null; }
  if ((tab === 'sales_portal' && !canAccessPortal('sales')) || (tab === 'collection' && !canAccessPortal('collection'))) { ui.setEntryPhase('selection'); return null; }

  return (
    <ThemeCtx.Provider value={T}>
      {/* fontSize: dynamic — Step 8b.5 Discovery Z category (user-pref scale, intentionally literal). */}
      <div className={shellStyles.shell} data-shell="v3" style={{ minHeight:"100vh", background:T.mainBg, fontFamily:T.bodyFont, color:T.textPrimary, fontWeight: T.fontPreset==="CL Brand" ? 500 : undefined, fontSize:`${(T.fontScale||1)*100}%`, "--app-header-height": `${shellChromeH}px`, "--page-bg": T.mainBg }}>

      {appOverlays}

      {/* SYS-10b: 10100, above every modal (DNA shell 10000, List-view Pictures 10010/10011, DnaSelect 10050),
          which hid every toast raised inside them; below the 11000+ hover popovers */}
      {toast && (
        <div data-app-toast style={{ position:"fixed", bottom:isMobile?80:24, left:"50%", transform:"translateX(-50%)", zIndex:'calc(var(--z-toast) + 101)', background:"var(--text)", color:"var(--card-bg)", padding:"var(--space-md) var(--space-xl)", borderRadius:'var(--radius-md)', fontWeight:'var(--weight-semi)', fontSize:'var(--text-xs)', whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:'var(--space-sm)', animation:"slideUp 0.3s ease-out", letterSpacing:"0.3px" }}>
          <span style={{ color:toast.err?"#ef4444":"var(--gold)", fontSize:'var(--text-sm)' }}>{toast.err?"⚠":"✓"}</span><span>{toast.msg}</span>
        </div>
      )}

      <div className={activePortal === 'catalog' ? catalogChromeStyles.catalogRoot : activePortal === 'developments' ? devsChromeStyles.developmentsRoot : undefined} style={{ display:"flex", flexDirection:"column", minHeight:"100vh" }}>
        <ShellRoot user={user} role={role} tab={tab} setTab={ui.setTab} perm={perm} showToast={ui.showToast} scopedDevsForDeadline={scopedDevsForDeadline} isDeadlineAdmin={isDeadlineAdmin} reloadDevs={reloadDevs}
            setShowGlobalSearch={ui.setShowGlobalSearch} setGlobalSearch={ui.setGlobalSearch} onBackToPortals={() => ui.setEntryPhase('selection')} activePortal={activePortal} devView={devView} activeDevs={activeDevs} devSettings={devSettings}
            filteredDevsList={filteredDevsList} filteredIgnoringStatusList={filteredIgnoringStatusList} setShowDevForm={ui.setShowDevForm} setShowAdd={setShowAdd} myProfile={myProfile} roleLabel={ROLES[role]?.label || role} onLogout={onLogout} canSettings={perm.canSettings} setShowShortcuts={ui.setShowShortcuts} catalog={{ search, setSearch, fBrand, setFBrand, fGender, setFGender, fType, setFType, fMat, setFMat, fShape, setFShape, fAssigned, setFAssigned, fCreatedBy, setFCreatedBy, sortBy, setSortBy, clearAllFilters, allBrands, genderOpts, typeOpts, matOpts, shapeOpts, assignedOpts, createdByOpts, setShowExport: ui.setShowExport, setShowImport, setShowAdd, totalCount, isCurrentlyFiltered, unfilteredTotal: stats.total, linkedDevMode, linkedDevQuery }} />

        {/* Every portal shares the current header, including compact screens.
            The shell's responsive height also owns the content offset. */}
        <div style={{ flex:1, marginTop:'var(--app-header-height)', padding:tab === "orders" || tab === "collection" || tab === "mail" || (activePortal === "developments" && devView === "mailIntel") ? 0 : isMobile?"12px":(tab==="qc"||tab==="dev_requests"||tab==="sales_portal"||tab==="projects"||tab==="lensBank"||tab==="acetateBank"||tab==="filesBank"||tab==="timeline"?"0":"14px 18px") }}>

          <TabRouter tab={tab} user={user} role={role} myProfile={myProfile} perm={perm} isMobile={isMobile}
            T={T} cardSt={cardSt} inputSt={inputSt} btnP={btnP} btnS={btnS} lblSt={lblSt} br={br} br75={br75}
            items={items} pagedItems={pagedItems} totalCount={totalCount} pageLoading={pageLoading} loaded={loaded}
            viewMode={viewMode} catalogCols={catalogCols} selectedItems={selectedItems} setSelectedItems={ui.setSelectedItems}
            setSelected={setSelected} setHoverPreview={ui.setHoverPreview} recentlyUploaded={recentlyUploaded}
            toggleFavorite={toggleFavorite} isFav={isFav} hasMore={hasMore} loadMoreItems={loadMoreItems}
            isCurrentlyFiltered={isCurrentlyFiltered} stats={stats} debouncedSearch={debouncedSearch}
            clearAllFilters={clearAllFilters}
            fBrand={fBrand} setFBrand={setFBrand} fGender={fGender} setFGender={setFGender} fType={fType} setFType={setFType}
            fMat={fMat} setFMat={setFMat} fShape={fShape} setFShape={setFShape} fStatus={fStatus} setFStatus={setFStatus}
            fAssigned={fAssigned} setFAssigned={setFAssigned} fCreatedBy={fCreatedBy} setFCreatedBy={setFCreatedBy}
            fields={fields} setFields={setFields} refreshAllItemsStats={refreshAllItemsStats} allItemsStats={allItemsStats} linksSlim={linksSlim}
            setItems={setItems} showToast={ui.showToast} setShowExport={ui.setShowExport} setShowImport={setShowImport}
            devs={devs} setDevs={setDevs} devSettings={devSettings} setDevSettings={setDevSettings}
            devView={devView} setDevViewPersist={ui.setDevView} devSearch={devSearch} devFilters={devFilters}
            devSort={devSort} showDevForm={showDevForm} setShowDevForm={ui.setShowDevForm}
            filteredDevsList={filteredDevsList} setFilteredDevsList={ui.setFilteredDevsList} setFilteredIgnoringStatusList={ui.setFilteredIgnoringStatusList}
            favorites={favorites} setFavorites={setFavorites} userNameMap={userNameMap} activeDevs={activeDevs}
            devOverdueCount={devOverdueCount} devRequests={devRequests}
            myDesignerNames={myDesignerNames} allDesignerNames={allDesignerNames} setTab={ui.setTab}
            devFormPrefill={devFormPrefill} devFormRequestLink={devFormRequestLink}
            onDevFormPrefillClear={() => { ui.setDevFormPrefill(null); ui.setDevFormRequestLink(null); }}
            theme={theme} setTheme={setTheme} setCatalogViewSize={setCatalogViewSize} catalogViewSize={catalogViewSize}
            showBrandManager={showBrandManager} setShowBrandManager={ui.setShowBrandManager}
            fetchPagedItems={fetchPagedItems} confirmDialog={confirmDialog}
            linkedDevsMap={linkedDevsMap} />
        </div>
      </div>

      {showAdd && <NewItemModal setShowAdd={setShowAdd} form={form} setForm={setForm} fields={fields} handleAdd={handleAdd} saving={saving} imageUploading={imageUploading} setImageUploading={setImageUploading} myProfile={myProfile} role={role} isMobile={isMobile} />}
      {selected && !editing && <FrameDetailModal selected={selected} setSelected={setSelected} setEditing={setEditing} setLightboxSrc={ui.setLightboxSrc} setFullScreen={ui.setFullScreen} setFsImgIdx={ui.setFsImgIdx} setFsZoomed={ui.setFsZoomed} setDevRequestItem={ui.setDevRequestItem} detailImgIdx={detailImgIdx} setDetailImgIdx={ui.setDetailImgIdx} fields={fields} perm={perm} role={role} comments={comments} commentsLoading={commentsLoading} commentText={commentText} setCommentText={setCommentText} addComment={addComment} handleDelete={handleDelete} userNameMap={userNameMap} isMobile={isMobile} T={T} linkedDevs={selected ? (linkedDevsMap.get(selected.id) || []) : []} setTab={ui.setTab} />}
      {devRequestItem && <DevRequestModal item={devRequestItem} onClose={()=>ui.setDevRequestItem(null)} onSaved={r=>setDevRequests(p=>[r,...p])} supabase={supabase} showToast={ui.showToast} T={T} cardSt={cardSt} btnP={btnP} btnS={btnS} inputSt={inputSt} lblSt={lblSt} designers={allDesignerNames} />}
      {editing && <EditFrameModal editing={editing} setEditing={setEditing} fields={fields} handleSaveEdit={handleSaveEdit} saving={saving} imageUploading={imageUploading} setImageUploading={setImageUploading} myProfile={myProfile} role={role} items={items} isMobile={isMobile} />}
      {showQuickAdd && <QuickAddModal showQuickAdd={showQuickAdd} setShowQuickAdd={setShowQuickAdd} quickForm={quickForm} setQuickForm={setQuickForm} form={form} setForm={setForm} setShowAdd={setShowAdd} fields={fields} handleAdd={handleAdd} saving={saving} T={T} cardSt={cardSt} btnP={btnP} btnS={btnS} inputSt={inputSt} lblSt={lblSt} myProfile={myProfile} role={role} />}
      {fullScreen && <FullScreenGallery fullScreen={fullScreen} setFullScreen={ui.setFullScreen} fsImgIdx={fsImgIdx} setFsImgIdx={ui.setFsImgIdx} fsZoomed={fsZoomed} setFsZoomed={ui.setFsZoomed} setSelected={setSelected} />}
      {showExport && <ExportModal onClose={() => ui.setShowExport(false)} showToast={ui.showToast} items={items} fields={fields} fetchAllItemsRaw={fetchAllItemsRaw} preselect={selectedItems.size ? selectedItems : null} T={T} cardSt={cardSt} btnP={btnP} btnS={btnS} br={br} br75={br75} />}
      {showImport && <ImportModal showImport={showImport} setShowImport={setShowImport} importPreview={importPreview} setImportPreview={setImportPreview} handleImportFile={handleImportFile} handleImportConfirm={handleImportConfirm} T={T} cardSt={cardSt} btnP={btnP} btnS={btnS} br75={br75} />}
      {hoverPreview && <HoverPreview hoverPreview={hoverPreview} />}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => ui.setLightboxSrc(null)} />}
      {devReqNotification && <DevRequestNotification request={devReqNotification} user={user} onView={() => { ui.setDevReqNotification(null); ui.setTab('dev_requests'); }} onDismiss={() => ui.setDevReqNotification(null)} />}
      {showShortcuts && <ShortcutsModal onClose={() => ui.setShowShortcuts(false)} />}


      {ConfirmDialogComponent}
      <NetworkErrorToast />
      <RealtimeTestHarness />
      </div>
      <SpeedInsights />
    </ThemeCtx.Provider>
  );
}
